import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { CallsSection } from "./CallsSection";
import { DownloadButton } from "./DownloadButton";
import { FacilityReportSelect } from "./FacilityReportSelect";
import { useFacilityReport } from "./hook";
import { OverallAISummarySection } from "./OverallAISummarySection";
import { ReviewsSection } from "./ReviewsSection";
import { RouteSearchSection } from "./RouteSearchSection";
import { SearchKeywordsSection } from "./SearchKeywordsSection";
import { WebsiteSection } from "./WebsiteSection";
import { AccountType } from "../../../models/AccountData";
import { OptionalRender } from "../../Common/OptionalRender";

import "./styles.scss";

interface IFacilityReportDetailsProps {
  facilityReportId: string;
}

export const FacilityReportDetails: React.FunctionComponent<
  IFacilityReportDetailsProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityReportsPage.facilityReportDetails.${key}`);

  const { facilityReportId } = props;

  const { data: facilityReport, isLoading } =
    useFacilityReport(facilityReportId);

  const reportData =
    facilityReport?.location_facility_reports_report_data?.data;

  // Downloading state is managed here because the indivdual sections
  // hide certain elements while downloading
  const [isDownloading, setIsDownloading] = useState(false);

  const history = useHistory();

  // TODO: Implement edit
  const handleEdit = () => {
    alert("edit not yet implemented");
  };

  const handleSelectReport = (id: number) => {
    history.push(`/facility-reports/${id}`);
  };

  return (
    <div className="facility-report-details">
      <div className="facility-report-details-title">
        <h2>{facilityReport?.location?.name}</h2>
      </div>
      <div className="line"></div>
      <div className="facility-report-details-controls">
        {facilityReport?.location?.name && facilityReportId && (
          <FacilityReportSelect
            locationId={facilityReport.location.id.toString()}
            currentReportId={Number(facilityReportId)}
            onSelectId={handleSelectReport}
          />
        )}
        {facilityReport && (
          <DownloadButton
            facilityReportData={facilityReport}
            isWorking={isDownloading}
            setIsWorking={setIsDownloading}
          />
        )}
        <OptionalRender mustBeOneOfRoles={[AccountType.ADMIN]}>
          <button className="btn btn-border" type="button" onClick={handleEdit}>
            {t("edit")}
          </button>
        </OptionalRender>
      </div>
      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="facility-report-details-content">
          <SearchKeywordsSection
            searchKeywords={reportData?.search_keywords}
            startDate={facilityReport?.start_date}
            endDate={facilityReport?.end_date}
          />
          <WebsiteSection
            website={reportData?.website}
            isDownloading={isDownloading}
          />
          <CallsSection
            calls={reportData?.calls}
            isDownloading={isDownloading}
          />
          <RouteSearchSection
            routeSearch={reportData?.route_search}
            isDownloading={isDownloading}
          />
          <ReviewsSection reviews={reportData?.reviews} />
          <OverallAISummarySection
            overallAISummary={reportData?.overall_ai_summary}
          />
        </div>
      )}
    </div>
  );
};
